import { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { getProductFromSessionStorage } from "src/utils/storage/auth";
import { useRouter } from "../useRouter";
import { applyCheckPromotionCode, getCashUserStatus } from "src/service/pay";
import { useSetRecoilState, useRecoilValue } from "recoil";
import { close30dGuideAtom, isShowPayAtom, navStepAtom } from "src/atoms";
import { notifyError } from "src/components/Common/Toast";
import { useProductFlags } from "../recoil/useProductFlags";
import { useProvisioningPoll } from "./console/useProvisioningPoll";
import { IGetUnregisterBillRes } from "src/service/types/pay.type";
import { IGetMyServiceListRes } from "src/service/types/console.type";
import {
  classifyUnregister,
  isCashWaiting,
  navStepFromPromotion,
  resolvePromotionRoute,
  shouldEnterUnregisterFlow,
  shouldPollProvisioning,
} from "./console/consoleInit.logic";

interface IUseCheckConsoleProps {
  isMyServiceLoading: boolean;
  unregisterPay: IGetUnregisterBillRes[]; // 프로모션 결제 안된 경우
  unregisterPromotion: IGetUnregisterBillRes[]; // 프로모션 결제 안된 경우
  setShowSiteNewModal: (show: boolean) => void; // 사이트 생성 모달 토글
  serviceList: IGetMyServiceListRes[]; // 서비스 리스트
  setMakeType: any; // 사이트 생성 타입
  setDoNotOpenGuide: any; // 30일 가이드 미열기 토글
  setServiceNotice: any; // 서비스 공지사항
  setCreatePercent: any; // 프로모션 생성 퍼센트
  detailIndex: number; // 서비스 인덱스 (현재는 eCo의 경우에만 해당 추후 viewType -> list 개발 시 사용)
  setShowMsgModal: (show: boolean) => void; // 메시지 모달 토글
  setIsDone: (isDone: boolean) => void; // 완료 토글
}
// 콘솔에서 초기값 세팅하는 Hook
export function useCheckConsole({
  isMyServiceLoading,
  unregisterPay,
  unregisterPromotion,
  serviceList,
  setShowSiteNewModal,
  setMakeType,
  setDoNotOpenGuide,
  setServiceNotice,
  setCreatePercent,
  detailIndex,
  setShowMsgModal,
  setIsDone,
}: IUseCheckConsoleProps) {
  const route = useRouter();
  const queryClient = useQueryClient();
  const setIsShowPay = useSetRecoilState(isShowPayAtom);
  const setNavStep = useSetRecoilState(navStepAtom);
  const close30dGuide = useRecoilValue(close30dGuideAtom);
  const { productKey, isOciProduct, isDspProduct, isDspmProduct } =
    useProductFlags();
  const [pollTargetUid, setPollTargetUid] = useState("");
  // 폴링 훅: 최상위에서 구독. 상태로 대상 UID를 제어
  useProvisioningPoll({
    serviceUid: pollTargetUid,
    onProgress: (status, percent) => {
      setServiceNotice(status);
      setCreatePercent(percent);
    },
    onComplete: () => {
      setServiceNotice("");
      setCreatePercent(0);
      setShowMsgModal(true);
    },
  });

  useEffect(() => {
    // --- 효과 적용 헬퍼 (라우팅/전역상태 변경) ---

    // 사이트 생성 모달 오픈 (미생성 청구 존재 시)
    const openSiteNew = (makeType: "MAKE_PAY" | "MAKE_PROMOTION") => {
      setShowSiteNewModal(true);
      setIsShowPay(false);
      setMakeType(makeType);
      setNavStep(3);
    };

    // 프로모션 코드 미보유(NONE) 시 OCI/비OCI에 따른 라우팅 적용
    const applyPromotionRoute = () => {
      const target = resolvePromotionRoute(isOciProduct, serviceList?.length > 0);
      if (target === "CONSOLE") route.goConsole();
      else if (target === "SITE_TYPE") route.goSiteType();
      else route.goSitePromotion({});
    };

    // 카드/프로모션 결제도, 미생성 청구도 없는 경우 → 프로모션 코드 상태 확인 후 라우팅
    const handlePromotionCheck = async () => {
      const result = await applyCheckPromotionCode({
        product: getProductFromSessionStorage(),
      });

      if (result && ["C_REQUEST"].includes(result)) {
        // 고객이 프리미엄코드 문의 신청한 상태
        route.goSitePromotion({});
      } else {
        if (result) setMakeType(result);
        applyPromotionRoute();
      }

      const nextNavStep = navStepFromPromotion(result);
      if (nextNavStep !== null) setNavStep(nextNavStep);

      setIsDone(true);
      setDoNotOpenGuide(close30dGuide);
    };

    // (OCI+ECO 또는 서비스 미보유) 진입 시: 미생성 청구 분류 → 모달/프로모션 체크
    const handleUnregisterFlow = async () => {
      try {
        const kind = classifyUnregister(unregisterPay, unregisterPromotion);
        if (kind === "PAY") {
          // 카드결제는 했는데 사이트 생성 안된 경우
          openSiteNew("MAKE_PAY");
        } else if (kind === "PROMOTION") {
          // 프로모션 결제는 했는데 사이트 생성 안된 경우
          openSiteNew("MAKE_PROMOTION");
          setIsDone(true);
        } else if (kind === "NONE") {
          // 카드결제도 프로모션결제도 안된 경우 → 프로모션 결제 체크
          await handlePromotionCheck();
        }
        // PENDING(목록 미로드): 아무 동작도 하지 않음(기존 동작 보존)
      } catch (error) {
        // 프로모션 체크 등 실패 시 폴백 라우팅
        if (isOciProduct) route.goSiteType();
        else route.goSitePromotion({});
      }
    };

    // 서비스 보유 시: 완료 처리 + (AWS 계열) 사이트 생성 프로그레스 폴링
    const handleExistingService = () => {
      const serviceStatus =
        serviceList && serviceList.length > 0
          ? serviceList[0].serviceStatus
          : "";

      setIsDone(true);
      setDoNotOpenGuide(close30dGuide);

      if (
        shouldPollProvisioning({
          serviceStatus,
          isOciProduct,
          isDspProduct,
          isDspmProduct,
        })
      ) {
        setPollTargetUid(serviceList[detailIndex]?.serviceUid ?? "");
      }
    };

    // --- 오케스트레이션 ---
    const init = async () => {
      // product값이 없는경우
      if (!sessionStorage.getItem("product")) {
        route.goLogin();
        return;
      }

      // 현금 결제 상태 조회
      // useGetCashStatus와 동일한 쿼리키로 요청하여 마운트 시 중복 호출을 dedup
      // (인플라이트 요청/캐시 공유). 최신성 유지를 위해 staleTime은 짧게 둔다.
      const product = getProductFromSessionStorage();
      const cashStatus = await queryClient.fetchQuery(
        ["getCashStatus", product],
        async () => await getCashUserStatus({ product: [product] }),
        { staleTime: 5_000 }
      );

      // 1) 현금 결제 대기 상태
      if (isCashWaiting(cashStatus, serviceList)) {
        setIsDone(true);
        setNavStep(2);
        return;
      }

      // 2) 미생성 청구/프로모션 분기 vs 서비스 보유 분기
      try {
        if (shouldEnterUnregisterFlow(isOciProduct, productKey, serviceList)) {
          await handleUnregisterFlow();
        } else {
          handleExistingService();
        }
      } catch (error: any) {
        if (error?.errorCode && error?.errorMessage) {
          notifyError(error.errorMessage);
        }
      }
    };

    // 1. 제품 리스트 체크
    // 2. 제품이 없는경우 빈 문자열을 받아온다
    if (!isMyServiceLoading) {
      init();
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMyServiceLoading, unregisterPay, unregisterPromotion, productKey]);
}
